import { AppId } from '../constants';
import { Route } from '../types';
import { getUrlWithPrefix } from './getUrlWithPrefix';

export interface CreatePageRouteOptions extends Omit<Route, 'path'> {
    // unique id for the page, if not passed it, it is calculated from the slug
    id?: string;
    // slug for the page, it should not include any app prefix (like `admin`, `ls`) and it should not start with a `/`
    slug: string;
    mode?: 'create' | 'view' | 'edit' | string;
    // IMPORTANT To be used only in specific location such as the search page. Use the slug as provided and do not add prefix such as `ls`
    useRawSlug?: boolean;
}

/**
 * Returns the URL for a given path, with a specific mode applied.
 * @param path - URL
 * @param mode - create, view, edit or any other custom
 * @returns URL
 */
const getRouteWithModeUrl = (path: string, mode?: CreatePageRouteOptions['mode']) => {
    if (!mode) {
        return path;
    }

    switch (mode) {
        case 'create':
            return `${path}/create`;
        case 'view':
            return `${path}/:id`;
        case 'edit':
            return `${path}/:id/edit`;
        default:
            return `${path}/:id/${mode}`;
    }
};

/**
 * Retrieves and generates the id for a given route
 */
const getRouteId = ({ id, slug, mode, appId }: CreatePageRouteOptions) => {
    const backOfficeIdPrefix = 'app.admin.';
    const frontOfficeIdPrefix = 'app.front.';
    const webviewIdPrefix = 'app.webview.';

    const generateId = (prefix: string) => {
        // If no id was provided, the final id is generated automatically from the slug, replacing
        // the '/' by a '-' and removing all ':'
        const generatedId = id ? `${prefix}${id}` : `${prefix}${slug.replace(/\//g, '-').replace(/:/g, '')}`;

        if (!mode) {
            return generatedId;
        }

        switch (mode) {
            case 'create':
                return `${generatedId}-create`;
            case 'view':
                return `${generatedId}-view`;
            case 'edit':
                return `${generatedId}-edit`;
            default:
                return `${generatedId}-${mode}`;
        }
    };

    switch (appId) {
        case AppId.backOffice:
            return generateId(backOfficeIdPrefix);
        case AppId.frontOffice:
            return generateId(frontOfficeIdPrefix);
        case AppId.webview:
            return generateId(webviewIdPrefix);
        default:
            return id;
    }
};

/**
 * Utility for creating a route for a given page based on the application that the page will be
 * hosted on. The function will add all the necessary prefixes to the given slug depending on
 * the app provided, as well as filling out the rest of the necessary options for it.
 */
export const createPageRoute = (createPageRouteOptions: CreatePageRouteOptions): Route => {
    const { appId, slug, mode, useRawSlug = false, ...options } = createPageRouteOptions;

    /**
     * Throw an error directly to let the developer know that the slug should not start with a '/'
     * We prefer this instead of cleaning up the slug here in order to avoid having routes
     * wrongly defined.
     */
    if (slug.startsWith('/')) {
        throw new Error(`slug ${slug} should not start with a '/'`);
    }

    const finalUrl = getUrlWithPrefix(appId, slug, useRawSlug);

    switch (appId) {
        case AppId.backOffice:
        case AppId.frontOffice:
        case AppId.webview:
        case AppId.support:
            return {
                path: getRouteWithModeUrl(finalUrl, mode),
                appId,
                legacyId: getRouteId(createPageRouteOptions),
                ...options,
            };
        default:
            return {
                appId,
                path: finalUrl,
                legacyId: getRouteId(createPageRouteOptions),
                ...options,
            };
    }
};
